import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isPlatformBrowser } from '@angular/common';
import { map, take, filter, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { selectAdminGuardData } from '../store/auth/auth.selectors';
import { MaintenanceService } from '../services/maintenance.service';
import { UserRole } from '../models/user.interface';

/**
 * Guard Manutenzione - Verifica se il sito è in manutenzione
 * Se attivo, permette l'accesso solo a:
 * - /login
 * - /backoffice/* (se admin autenticato)
 * - Admin autenticati possono accedere normalmente
 */
export const maintenanceGuard: CanActivateFn = (route, state) => {
	const platformId = inject(PLATFORM_ID);
	const router = inject(Router);
	const store = inject(Store);
	const maintenanceService = inject(MaintenanceService);

	// SSR: Passa sempre la guard (il browser farà il controllo reale)
	if (!isPlatformBrowser(platformId)) {
		return of(true);
	}

	const url = state.url;

	// Route sempre permesse anche durante la manutenzione
	const allowedRoutes = ['/login', '/maintenance'];
	const isAllowedRoute = allowedRoutes.some(route => url.startsWith(route));
	const isBackofficeRoute = url.startsWith('/backoffice');

	// Se è una route permessa, passa sempre
	if (isAllowedRoute) {
		return of(true);
	}

	// Controlla lo stato di manutenzione
	return maintenanceService.getMaintenanceStatus().pipe(
		switchMap(maintenanceStatus => {
			// Se la manutenzione non è attiva, permetti l'accesso
			if (!maintenanceStatus.isMaintenanceMode) {
				return of(true);
			}

			// Se la manutenzione è attiva, controlla se l'utente è admin
			return store.select(selectAdminGuardData).pipe(
				filter(authData => authData.isInitialized && !authData.isLoading),
				take(1),
				switchMap(authData => {
					// Se l'utente è admin autenticato, permetti l'accesso
					if (authData.isAuthenticated && authData.role === UserRole.ADMIN) {
						return of(true);
					}

					// Se è una route del backoffice ma l'utente non è admin, reindirizza alla manutenzione
					if (isBackofficeRoute) {
						router.navigate(['/maintenance']);
						return of(false);
					}

					// Altrimenti, reindirizza alla pagina di manutenzione
					router.navigate(['/maintenance']);
					return of(false);
				}),
				catchError(() => {
					// In caso di errore, reindirizza alla manutenzione per sicurezza
					router.navigate(['/maintenance']);
					return of(false);
				})
			);
		}),
		catchError(() => {
			// In caso di errore nel recupero dello stato, assumi che non ci sia manutenzione
			// per evitare di bloccare l'accesso in caso di problemi di rete
			return of(true);
		})
	);
};

